import { FC } from 'react'

import { Box, GlobalStyles, Paper, Typography } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'

import { useGetProfitChartsQuery } from 'services/dashboard-analytics/api'
import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'
import { chartsTooltipClasses } from '@mui/x-charts'

interface ProfitChartProps {
  filters: DashboardAnalyticsFilter[]
}

const ProfitChart: FC<ProfitChartProps> = ({ filters }) => {
  const { data: profitChartsData } = useGetProfitChartsQuery({
    filters: filters,
  },
    {
      refetchOnMountOrArgChange: true,
    })
  const chartData = profitChartsData

  const dates = chartData?.map((item) => new Date(item.date).toLocaleDateString('en-CA')) || []
  const salesData = chartData?.map((item) => item.totalSold) || []
  const purchasesData = chartData?.map((item) => -item.totalBought) || []

  const chartDataWithMeta =
    chartData?.map((item) => ({
      date: item?.date,
      sales: item?.totalSold,
      purchases: item?.totalBought,
      minPrice: item?.minPrice || 0,
      maxPrice: item?.maxPrice || 0,
      avgPrice: item?.avgPrice || 0,
      ticketsCount: item?.ticketsCnt,
      profitEur: item?.profitEur,
      profitPercent: item?.profitPercent,
    })) || []

  return (
    <Paper
      sx={{
        height: '100%',
        p: '16px',
        py: '8px',
        boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
      }}
    >
       <GlobalStyles
        styles={{
          [`.${chartsTooltipClasses.valueCell}`]: { whiteSpace: 'pre-line' },
          [`.${chartsTooltipClasses.row}`]: { alignItems: 'flex-start' }
        }}
      />
      <Typography variant="h3">Profit Chart</Typography>

      <Box sx={{ height: 'calc(100% - 60px)' }}>
        <BarChart
          xAxis={[
            {
              data: dates,
              scaleType: 'band',
              valueFormatter: (value) => value,
            },
          ]}
          series={[
            {
              data: salesData,
              label: 'Sales',
              color: '#4caf50',
              stack: 'total',
              valueFormatter: (value) => {
                const index = salesData.indexOf(value as number)
                const meta = chartDataWithMeta[index]
                if (!meta) return `€${(value || 0).toFixed(2)}`

                return [
                  `Tickets: ${meta.ticketsCount}`,
                  `Min: €${meta.minPrice}`,
                  `Max: €${meta.maxPrice}`,
                  `Avg Price: €${meta.avgPrice}`,
                  `Profit: €${meta.profitEur} (${meta.profitPercent}%)`,
                ].join('\n');
              },
            },
            {
              data: purchasesData,
              label: 'Purchases',
              color: '#f44336',
              stack: 'total',
              valueFormatter: (value) => {
                const index = purchasesData.indexOf(value as number)
                const meta = chartDataWithMeta[index]
                if (!meta) return `€${Math.abs(value || 0).toFixed(2)}`

                return [
                  `Tickets: ${meta.ticketsCount}`,
                  `Min: €${meta.minPrice}`,
                  `Max: €${meta.maxPrice}`,
                  `Avg Price: €${meta.avgPrice}`,
                  `Profit: €${meta.profitEur} (${meta.profitPercent}%)`,
                ].join('\n');
              },
            },
          ]}
          height={400}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'right' },
              itemMarkWidth: 8,
              itemMarkHeight: 8,
              markGap: 5,
              itemGap: 10,
            },
          }}
          tooltip={{
            trigger: 'item',
          }}
        />
      </Box>
    </Paper>
  )
}

export default ProfitChart
